import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { getTeamLogoUrl } from '../utils/logoUrl';
import { formatDateShort } from '../utils/dateUtils';
import {
  getPredictionOutcome,
  getOutcomeCardClasses,
  getOutcomeAccentColor,
  getOutcomeMutedTextColor,
  getOutcomeBorderColor,
  getOutcomeLabel,
} from '../utils/predictionOutcomeUtils';
import { formatPoints } from '../utils/scoreUtils';

interface Prediction {
  id: string;
  homeGoals: number;
  awayGoals: number;
  isBanker: boolean;
  points?: number;
  match: {
    id: string;
    homeTeam: {
      id: string;
      name: string;
      shortName?: string;
      logoUrl?: string;
    };
    awayTeam: {
      id: string;
      name: string;
      shortName?: string;
      logoUrl?: string;
    };
    matchDate: string;
    homeGoals?: number;
    awayGoals?: number;
    status: string;
  };
  season: {
    id: string;
    name: string;
  };
  matchGroup: {
    id: string;
    name: string;
  };
}

const outcomeBadgeClasses = {
  exact: 'bg-amber-900/10 text-amber-950 dark:bg-black/20 dark:text-amber-950',
  correct: 'bg-green-900/10 text-green-900 dark:bg-black/20 dark:text-green-50',
  wrong: 'bg-red-900/10 text-red-900 dark:bg-black/20 dark:text-red-50',
} as const;

const RecentPredictionsWidget: React.FC = () => {
  const { user } = useAuth();
  const [recentPredictions, setRecentPredictions] = useState<Prediction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchRecentPredictions = async () => {
      // Check if we have cached data with expiration
      const cachedData = sessionStorage.getItem('recent_predictions_data');
      const cacheTimestamp = sessionStorage.getItem('recent_predictions_timestamp');
      
      if (cachedData && cacheTimestamp) {
        try {
          const timestamp = parseInt(cacheTimestamp);
          const now = Date.now();
          const fifteenMinutes = 15 * 60 * 1000; // 15 minutes in milliseconds
          
          // Check if cache is still valid (less than 15 minutes old)
          if (now - timestamp < fifteenMinutes) {
            const parsed = JSON.parse(cachedData);
            setRecentPredictions(parsed);
            setLoading(false);
            return;
          } else {
            // Cache expired, remove it
            sessionStorage.removeItem('recent_predictions_data');
            sessionStorage.removeItem('recent_predictions_timestamp');
          }
        } catch (e) {
          // If parsing fails, remove invalid cache
          sessionStorage.removeItem('recent_predictions_data');
          sessionStorage.removeItem('recent_predictions_timestamp');
        }
      }
      
      try {
        setLoading(true);
        setError(null);
        
        const response = await apiClient.get('/predictions');
        const allPredictions = response.data;
        
        // Filter for scored predictions (past matches with results)
        const now = new Date();
        const scoredPredictions = allPredictions
          .filter((prediction: Prediction) => {
            const matchDate = new Date(prediction.match.matchDate);
            const hasResult = prediction.match.homeGoals !== undefined && 
                             prediction.match.homeGoals !== null &&
                             prediction.match.awayGoals !== undefined && 
                             prediction.match.awayGoals !== null;
            return hasResult && matchDate < now;
          })
          .sort((a: Prediction, b: Prediction) => {
            // Sort by match date descending (most recent first)
            return new Date(b.match.matchDate).getTime() - new Date(a.match.matchDate).getTime();
          })
          .slice(0, 12); // Get top 12 most recent
        
        setRecentPredictions(scoredPredictions);
        
        // Cache the data with timestamp
        sessionStorage.setItem('recent_predictions_data', JSON.stringify(scoredPredictions));
        sessionStorage.setItem('recent_predictions_timestamp', Date.now().toString());
      } catch (err) {
        console.error('Error fetching recent predictions:', err);
        setError('Failed to load recent predictions');
      } finally {
        setLoading(false);
      }
    };

    fetchRecentPredictions();
  }, [user]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto mt-8 px-2 md:px-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-6 w-48"></div>
          <div className="relative md:-mx-6 md:px-6 md:overflow-hidden">
            <div className="overflow-x-auto scrollbar-hide -mx-2 px-2 md:mx-0 md:px-0">
              <div className="flex gap-4 pr-12 md:pr-16">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-44 bg-gray-300 dark:bg-gray-600 rounded flex-shrink-0 w-[60vw] md:w-[280px]"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || recentPredictions.length === 0) {
    return null; // Don't show anything if there's an error or no predictions
  }

  return (
    <div className="max-w-5xl mx-auto mt-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
          Recent Predictions
        </h2>
        <Link 
          to="/my-predictions"
          className="text-sm font-medium"
          style={{ color: 'var(--highlight)' }}
        >
          View All Predictions →
        </Link>
      </div>

      <div className="relative mb-8 md:-mx-6 md:px-6 md:overflow-hidden">
        <div className="overflow-x-auto scrollbar-hide -mx-2 px-2 md:mx-0 md:px-0">
          <div className="flex gap-4 pr-12 md:pr-16" style={{ minWidth: 'max-content' }}>
            {recentPredictions.map((prediction) => {
              const { match } = prediction;
              const outcome = getPredictionOutcome(
                match.homeGoals,
                match.awayGoals,
                prediction,
                false
              );
              const accentClass = getOutcomeAccentColor(outcome);
              const mutedClass = getOutcomeMutedTextColor(outcome);
              const borderClass = getOutcomeBorderColor(outcome);
              const outcomeLabel = getOutcomeLabel(outcome);
              const points = prediction.points ?? 0;

              return (
                <Link
                  key={prediction.id}
                  to={`/predictions/${prediction.match.id}`}
                  className={`card rounded-lg overflow-hidden p-4 hover:shadow-lg transition-shadow flex-shrink-0 w-[60vw] md:w-[280px] md:min-w-[280px] ${getOutcomeCardClasses(outcome, true)}`}
                >
                  <div className={`flex items-center justify-between gap-2 text-xs mb-2 ${mutedClass}`}>
                    <span>{formatDateShort(match.matchDate)}</span>
                    <span className="truncate">{prediction.season.name}</span>
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                      {match.homeTeam.logoUrl && (
                        <img
                          src={getTeamLogoUrl(match.homeTeam.logoUrl)}
                          alt=""
                          className="w-6 h-6 object-contain flex-shrink-0"
                        />
                      )}
                      <span className={`text-xs font-semibold truncate ${accentClass}`}>
                        {match.homeTeam.shortName || match.homeTeam.name}
                      </span>
                    </div>

                    <div className={`text-lg font-bold tabular-nums flex-shrink-0 ${accentClass}`}>
                      {match.homeGoals} - {match.awayGoals}
                    </div>

                    <div className="flex items-center gap-1.5 flex-1 min-w-0 justify-end">
                      <span className={`text-xs font-semibold truncate text-right ${accentClass}`}>
                        {match.awayTeam.shortName || match.awayTeam.name}
                      </span>
                      {match.awayTeam.logoUrl && (
                        <img
                          src={getTeamLogoUrl(match.awayTeam.logoUrl)}
                          alt=""
                          className="w-6 h-6 object-contain flex-shrink-0"
                        />
                      )}
                    </div>
                  </div>

                  <div className={`pt-4 border-t ${borderClass}`}>
                    <div className="flex flex-col items-center text-center gap-0.5">
                      <p className={`text-[10px] font-bold uppercase tracking-[0.14em] ${mutedClass}`}>
                        Your prediction
                      </p>
                      <div className={`flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 ${accentClass}`}>
                        <span className="text-xl font-bold tabular-nums leading-none tracking-tight">
                          {prediction.homeGoals}
                          <span className="mx-1.5 font-light opacity-30">-</span>
                          {prediction.awayGoals}
                        </span>
                        {prediction.isBanker && (
                          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-black/10 dark:bg-black/20">
                            Banker
                          </span>
                        )}
                      </div>
                      {outcomeLabel && (
                        <div
                          className={`inline-flex items-center gap-1.5 mt-4 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${outcomeBadgeClasses[outcome as keyof typeof outcomeBadgeClasses]}`}
                        >
                          <span className="uppercase tracking-wide">{outcomeLabel}</span>
                          <span className="opacity-35">·</span>
                          <span className="tabular-nums">{formatPoints(points)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
        {/* Gradient fade overlay on the right side */}
        <div 
          className="absolute top-0 bottom-0 pointer-events-none z-10 w-8 md:w-16 bg-gradient-to-l from-[#eff5f6] via-[#eff5f6]/70 to-transparent dark:from-[#0C0E12] dark:via-[#0C0E12]/70 dark:to-transparent -right-2 md:right-0"
        ></div>
      </div>

{/*       <div className="text-center mb-8">
        <Link 
          to="/my-predictions"
          className="text-sm font-medium"
          style={{ color: 'var(--highlight)' }}
        >
          View All Predictions →
        </Link>
      </div> */}
    </div>
  );
};

export default RecentPredictionsWidget;

